import { useState, useRef, useEffect, useCallback } from 'react';
import { DROPDOWN_CONFIG } from '../constants';

const useInfiniteScrollItems = ({
  totalItems = 0,
  enabled = true,
  initialCount = DROPDOWN_CONFIG.INITIAL_ITEMS_TO_SHOW,
  increment = DROPDOWN_CONFIG.ITEMS_PER_LOAD,
  threshold = DROPDOWN_CONFIG.SCROLL_THRESHOLD,
}) => {
  const [visibleItemsCount, setVisibleItemsCount] = useState(initialCount);
  const scrollRafRef = useRef(null);

  const safeTotalItems = Number.isFinite(totalItems) ? Math.max(totalItems, 0) : 0;
  const hasMore = visibleItemsCount < safeTotalItems;

  useEffect(
    () => () => {
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    },
    [],
  );

  const resetVisibleItems = useCallback(() => {
    setVisibleItemsCount(initialCount);
  }, [initialCount]);

  const handleScroll = useCallback(
    (event) => {
      if (!enabled || !hasMore) return;
      if (scrollRafRef.current) return;

      const scrollContainer = event.currentTarget;
      if (!scrollContainer) return;

      scrollRafRef.current = requestAnimationFrame(() => {
        scrollRafRef.current = null;
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        if (scrollHeight <= clientHeight) return;

        if (scrollHeight - scrollTop - clientHeight < threshold) {
          setVisibleItemsCount((prev) => Math.min(prev + increment, safeTotalItems));
        }
      });
    },
    [enabled, hasMore, increment, safeTotalItems, threshold],
  );

  return {
    visibleItemsCount,
    hasMore,
    resetVisibleItems,
    handleScroll,
  };
};

export default useInfiniteScrollItems;
